import { type JSX, useState } from 'react';
import type { ZodIssue } from 'zod';

import type { MobiLauncherConfig as Config } from '@schema/index.js';

import type { ParseStatus } from '../state.js';

import { LivePreview } from './LivePreview.js';

/**
 * Section IDs in the App nav. Kept in sync with App.tsx — duplicating the
 * union is acceptable here because PreviewPane is the only consumer and
 * a wider shared types module would be overkill for ~10 strings.
 */
export type SectionId =
  | 'profile-basics'
  | 'tiles'
  | 'theme'
  | 'status-bar'
  | 'admin'
  | 'features'
  | 'schedules'
  | 'device'
  | 'telemetry'
  | 'remote';

export interface JumpTarget {
  section: SectionId;
  /** Index into config.profiles[] when the issue is profile-scoped. */
  profileIndex?: number;
}

export interface PreviewPaneProps {
  config: Config;
  parseStatus: ParseStatus;
  /** Callback to navigate the editor to the offending section / profile. */
  onJumpTo?: (target: JumpTarget) => void;
  /** Bubbled up from LivePreview when the iframe has fired preview-ready. */
  onPreviewReady?: () => void;
}

type Tab = 'live' | 'json' | 'validation';

/**
 * Right pane of the builder. Three tabs:
 *   - Live: iframe-hosted launcher rendering against the current config
 *   - JSON: serialized config + Copy button
 *   - Validation: clickable list of Zod issues — each row jumps the editor
 *     to the field that caused the problem.
 *
 * The validation badge is always visible (top right) so even on the Live
 * or JSON tab you can tell at a glance whether the current edit parses.
 */
export function PreviewPane({ config, parseStatus, onJumpTo, onPreviewReady }: PreviewPaneProps): JSX.Element {
  const [tab, setTab] = useState<Tab>('live');
  const [copied, setCopied] = useState(false);

  const text = JSON.stringify(config, null, 2);
  const issueCount = parseStatus.kind === 'invalid' ? parseStatus.issues.length : 0;

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Some browsers block clipboard in non-secure contexts; user can
      // select-all + copy manually.
    }
  }

  return (
    <aside className="preview">
      <header className="preview__head">
        <div className="preview__tabs" role="tablist">
          <TabButton active={tab === 'live'} onClick={() => setTab('live')}>
            Live
          </TabButton>
          <TabButton active={tab === 'json'} onClick={() => setTab('json')}>
            JSON
          </TabButton>
          <TabButton active={tab === 'validation'} onClick={() => setTab('validation')}>
            Validation{issueCount > 0 && ` (${issueCount})`}
          </TabButton>
        </div>
        <div className="preview__actions">
          <span
            className={`preview__badge preview__badge--${parseStatus.kind}`}
            data-testid="parse-status"
            role="status"
            aria-live="polite"
          >
            {parseStatus.kind === 'valid' ? '✓ valid' : '⚠ invalid'}
          </span>
          {tab === 'json' && (
            <button
              type="button"
              className="preview__copy"
              onClick={() => {
                void copy();
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          )}
        </div>
      </header>
      {tab === 'live' && (
        <div className="preview__body preview__body--live" data-testid="live-pane">
          <LivePreview
            config={config}
            {...(onPreviewReady !== undefined && { onReady: onPreviewReady })}
          />
        </div>
      )}
      {tab === 'json' && (
        <pre className="preview__body" data-testid="config-preview">
          {text}
        </pre>
      )}
      {tab === 'validation' && (
        <div className="preview__body preview__body--validation" data-testid="validation-preview">
          {parseStatus.kind === 'valid' ? (
            <p className="validation__ok">✓ No validation errors. Config is ready to download.</p>
          ) : (
            <ValidationList issues={parseStatus.issues} {...(onJumpTo ? { onJumpTo } : {})} />
          )}
        </div>
      )}
    </aside>
  );
}

function ValidationList({
  issues,
  onJumpTo,
}: {
  issues: ZodIssue[];
  onJumpTo?: (target: JumpTarget) => void;
}): JSX.Element {
  return (
    <ul className="validation__list">
      {issues.map((issue, i) => {
        const target = pathToTarget(issue.path);
        return (
          <li key={`${i}-${issue.path.join('.')}`} className="validation__row">
            <div className="validation__row-main">
              <code className="validation__path">{formatPath(issue.path)}</code>
              <p className="validation__message">{issue.message}</p>
            </div>
            {target !== null && onJumpTo !== undefined && (
              <button
                type="button"
                className="validation__jump"
                onClick={() => onJumpTo(target)}
                title={`Jump to ${target.section}`}
              >
                Jump →
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Format a Zod issue path as `a.b[0].c` — mirrors the JSON-path style
 * users see in dev tools rather than Zod's array-of-segments form.
 */
function formatPath(path: (string | number)[]): string {
  if (path.length === 0) return '(root)';
  return path
    .map((seg, i) =>
      typeof seg === 'number' ? `[${seg}]` : i === 0 ? String(seg) : `.${String(seg)}`,
    )
    .join('');
}

/**
 * Map a Zod issue path to the editor section that owns the offending
 * field. Returns null for root-level fields that don't have a dedicated
 * section (those still render in the list, just without a Jump button).
 */
function pathToTarget(path: (string | number)[]): JumpTarget | null {
  const [head, ...rest] = path;

  if (head === 'profiles' && typeof rest[0] === 'number') {
    const profileIndex = rest[0];
    const key = rest[1];
    if (key === 'grid') return { section: 'tiles', profileIndex };
    if (key === 'theme') return { section: 'theme', profileIndex };
    if (key === 'statusBar') return { section: 'status-bar', profileIndex };
    // name / id / anything else on the profile → profile basics
    return { section: 'profile-basics', profileIndex };
  }
  if (head === 'schedules') return { section: 'schedules' };
  if (head === 'adminAccess') return { section: 'admin' };
  if (head === 'enabledFeatures') return { section: 'features' };
  if (head === 'device') return { section: 'device' };
  if (head === 'telemetry') return { section: 'telemetry' };
  if (head === 'remoteConfig') return { section: 'remote' };
  // Root-level deployment fields → profile basics (where they live in the UI)
  if (head === 'tenantId' || head === 'deploymentName' || head === 'defaultProfileId') {
    return { section: 'profile-basics' };
  }
  // schemaVersion, meta — no dedicated editor surface
  return null;
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={`preview__tab${active ? ' preview__tab--active' : ''}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
